import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import { useDateTime } from '../../../contexts/DateTimeContext';

const TYPES = [
  { value: 'daily', labelKey: 'tasks.recurrence_daily' },
  { value: 'weekly', labelKey: 'tasks.recurrence_weekly' },
  { value: 'biweekly', labelKey: 'tasks.recurrence_biweekly' },
  { value: 'monthly', labelKey: 'tasks.recurrence_monthly' },
];

const RecurrencePicker = ({ value, onChange, children, disabled = false }) => {
  const { t } = useTranslation();
  const { weekdayLetters } = useDateTime();
  // days_of_week: 0 = Monday.
  const dayLabels = weekdayLetters(1);
  const [open, setOpen] = useState(false);
  const [localDays, setLocalDays] = useState(value?.days_of_week || []);

  const handleTypeChange = (type) => {
    const days = (type === 'weekly' || type === 'biweekly') ? localDays : [];
    onChange({ type, interval: 1, days_of_week: days });
  };

  const handleDayToggle = (dayIndex) => {
    const newDays = localDays.includes(dayIndex)
      ? localDays.filter(d => d !== dayIndex)
      : [...localDays, dayIndex];
    setLocalDays(newDays);
    if (value) {
      onChange({ ...value, days_of_week: newDays });
    }
  };

  const handleClear = () => {
    onChange(null);
    setLocalDays([]);
    setOpen(false);
  };

  const getLabel = () => {
    if (!value) return t('tasks.once');
    const type = TYPES.find((x) => x.value === value.type);
    return type ? t(type.labelKey) : value.type;
  };

  const defaultTrigger = (
    <button disabled={disabled} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' :
      value ? 'text-amber-700 dark:text-amber-400 bg-amber-500/10' : 'text-muted-foreground hover:bg-accent/60'
    }`}>
      <RefreshCw className="w-3.5 h-3.5" />
      <span>{getLabel()}</span>
    </button>
  );

  return (
    // The days of the task it shows now: a picker kept from another task must not send that one's days.
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setLocalDays(value?.days_of_week || []); }}>
      <PopoverTrigger asChild>
        {children || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3 bg-accent border-border max-md:!w-screen max-md:!max-w-none max-md:!rounded-b-none" align="start" sideOffset={4}>
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-xs text-muted-foreground font-medium">{t('tasks.repeat')}</span>
          {value && (
            <button onClick={handleClear} className="text-[10px] text-muted-foreground hover:text-foreground">
              {t('common.clear')}
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-1.5 mb-2.5">
          {TYPES.map(type => (
            <button
              key={type.value}
              onClick={() => handleTypeChange(type.value)}
              className={`py-1.5 rounded-md text-xs font-medium border transition-colors ${
                value?.type === type.value
                  ? 'border-amber-500 bg-amber-500/10 text-foreground'
                  : 'border-border bg-accent/60 text-muted-foreground hover:border-input'
              }`}
            >
              {t(type.labelKey)}
            </button>
          ))}
        </div>
        {value && (value.type === 'weekly' || value.type === 'biweekly') && (
          <div>
            <p className="text-[10px] text-muted-foreground mb-1.5">{t('ui_task.recurrence.days')}</p>
            <div className="flex gap-1">
              {dayLabels.map((day, i) => (
                <button
                  key={i}
                  onClick={() => handleDayToggle(i)}
                  className={`w-7 h-7 rounded-full text-[10px] font-medium border transition-colors ${
                    localDays.includes(i)
                      ? 'border-amber-500 bg-amber-500/10 text-foreground'
                      : 'border-border bg-accent/60 text-muted-foreground hover:border-input'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default RecurrencePicker;
